import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button, type ButtonProps } from './button';
import { cn } from '@/lib/utils';

interface RefreshButtonProps extends Omit<ButtonProps, 'onClick'> {
  onRefresh: () => void;
  isRefreshing?: boolean;
  showText?: boolean;
  refreshText?: string;
  refreshingText?: string;
}

/**
 * Reusable refresh button component with consistent styling and behavior
 */
export const RefreshButton = forwardRef<HTMLButtonElement, RefreshButtonProps>(
  (
    {
      onRefresh,
      isRefreshing = false,
      showText = true,
      refreshText,
      refreshingText,
      variant = 'outline',
      size = 'sm',
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    const displayRefreshText = refreshText || t('common.refresh');
    const displayRefreshingText = refreshingText || t('common.refreshing');

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        onClick={onRefresh}
        disabled={disabled || isRefreshing}
        className={cn(className)}
        data-testid="refresh-button"
        aria-label={isRefreshing ? displayRefreshingText : displayRefreshText}
        {...props}
      >
        <RefreshCw
          className={cn(
            'h-4 w-4',
            showText && 'mr-2',
            isRefreshing && 'animate-spin'
          )}
          data-lucide="refresh-cw"
        />
        {showText && (isRefreshing ? displayRefreshingText : displayRefreshText)}
      </Button>
    );
  }
);

RefreshButton.displayName = 'RefreshButton';